/**
 * Comando unificado de tarefa (D05, §6, ADR-0008).
 *
 * ── A origem é o que só existe aqui ────────────────────────────────────────
 *
 * O ADR-0008 mantém a tarefa autoritativa no Twenty, mas a ORIGEM é contexto
 * do CliqCRM: *"automação, SLA, conector, manual — porque isso é contexto do
 * CliqCRM e não existe no modelo do Twenty"*. Uma tarefa criada por regra de
 * SLA e uma criada à mão são indistinguíveis na engine, e a diferença é
 * justamente o que a revenda quer auditar.
 *
 * Por isso `origem` é OBRIGATÓRIA na criação, sem valor padrão. Um padrão
 * `manual` faria toda tarefa de automação nascer mentindo, e ninguém
 * perceberia — a mentira só apareceria meses depois, num relatório.
 *
 * ── `atrasada` é derivada na leitura, nunca guardada ───────────────────────
 *
 * Depende do horário de atendimento do tenant (A07 Q04), e a D01 já registrou
 * o porquê de não ser coluna: congelar a derivação erraria toda vez que o
 * horário mudasse. A resposta traz `atrasada` E `atrasada_desde` — o segundo
 * é o que permite a tela explicar o primeiro em vez de só pintar de vermelho.
 */

import { z } from 'zod';
import { IdSchema } from './comum.js';
import { MotivoDeNaoReceberSchema } from './responsavel.js';
import { InstanteSchema } from './valores.js';

/**
 * De onde a tarefa veio. Fechado de propósito: uma origem nova é decisão de
 * produto, e um campo livre viraria dezoito grafias de "automação".
 */
export const OrigemDaTarefaSchema = z.enum(['manual', 'automacao', 'sla', 'conector']);
export type OrigemDaTarefa = z.infer<typeof OrigemDaTarefaSchema>;

/**
 * Quanto a tarefa corre.
 *
 * ── Quatro degraus, e por que não tres nem cinco ──────────────────────────
 *
 * Tres nao separam "faça hoje" de "faça agora", que sao coisas diferentes na
 * mesa de um vendedor. Cinco fazem duas pessoas classificarem a mesma tarefa
 * de forma diferente, e aí a ordem deixa de ordenar.
 *
 * ── Ela mora AQUI, e nao no motor ─────────────────────────────────────────
 *
 * O modelo de tarefa do Twenty (A01) nao tem prioridade. Pelo ADR-0008 isso a
 * torna contexto do CliqCRM, como a origem — com uma diferença que decidiu
 * onde ela é guardada: origem é história, escrita uma vez; prioridade MUDA, e
 * a F02 lista "alterar prioridade" entre as ações de automação.
 */
export const PrioridadeDaTarefaSchema = z.enum(['baixa', 'normal', 'alta', 'urgente']);
export type PrioridadeDaTarefa = z.infer<typeof PrioridadeDaTarefaSchema>;

export const AlvoDaTarefaSchema = z.object({
  tipo: z.enum(['cliente', 'oportunidade', 'empresa']),
  /** Canônico (§5.1) — o cliente 360 e o funil falam em canônico, não em id de engine. */
  id: IdSchema,
});

export type AlvoDaTarefa = z.infer<typeof AlvoDaTarefaSchema>;

export const CriarTarefaSchema = z.object({
  titulo: z.string().min(1).max(500),
  corpo: z.string().max(20_000).nullish(),
  prazo_em: InstanteSchema.nullish(),
  /** Id do membro do workspace na engine — campo de referência (§3.2). */
  responsavel_referencia_externa: z.string().nullish(),
  alvo: AlvoDaTarefaSchema.nullish(),
  /**
   * Ausente vira `normal` — e isso NAO é o mesmo caso da origem.
   *
   * A origem é obrigatória porque um padrão faria toda tarefa de automação
   * nascer mentindo. Aqui o padrão nao mente sobre nada: "nao classificada" e
   * "prioridade normal" sao a mesma afirmação, e obrigar o campo faria quem
   * cria escolher um valor que ele nao pensou — que é como uma escala vira
   * ruído.
   */
  prioridade: PrioridadeDaTarefaSchema.optional(),
  origem: z.object({
    tipo: OrigemDaTarefaSchema,
    /**
     * O que disparou, quando não foi uma pessoa: id da regra de automação, do
     * SLA, do conector. *"Criada por automação"* não basta para auditar; *"pela
     * regra tal"* basta.
     */
    referencia: z.string().max(200).nullish(),
    /** Texto livre para o humano que vai ler a trilha depois. */
    motivo: z.string().max(500).nullish(),
  }),
});

export type CriarTarefa = z.infer<typeof CriarTarefaSchema>;

/**
 * `PATCH /v1/tasks/{id}` — o que se pode mudar depois de criada.
 *
 * ── Tudo opcional, e é a diferença entre mexer e não mexer ────────────────
 *
 * Campo AUSENTE não é tocado; campo presente com `null` limpa. A distinção
 * importa: um formulário que manda o objeto inteiro a cada salvamento apagaria
 * o prazo de quem só quis trocar o título, e o servidor aceitaria calado. É o
 * mesmo defeito que o `jsonb` de configuração já custou.
 *
 * Por isso `.optional()` e não `.nullish()` no que é texto: `nullish` aceita
 * ausente e nulo como a mesma coisa, e aqui eles são opostos.
 *
 * ── O que NÃO entra ───────────────────────────────────────────────────────
 *
 * O ALVO. Mudar de quem é a tarefa é outra operação: no Twenty o vínculo é
 * uma entidade separada (`taskTarget`), e trocá-lo é apagar um e criar outro,
 * com o mesmo risco de meia-transação que a criação já carrega.
 *
 * A ORIGEM. Ela é história — por que esta tarefa existe — e história não se
 * corrige; no máximo se explica na seguinte.
 *
 * O STATUS. Concluir tem rota própria, idempotente, e reabrir não existe.
 */
export const AlterarTarefaSchema = z
  .object({
    titulo: z.string().min(1).max(500).optional(),
    /** `null` limpa a descrição; ausente não a toca. */
    corpo: z.string().max(20_000).nullable().optional(),
    /** `null` tira o prazo — e com ele, a tarefa deixa de poder atrasar. */
    prazo_em: InstanteSchema.nullable().optional(),
    /** `null` devolve a tarefa à fila, sem dono. */
    responsavel_referencia_externa: z.string().nullable().optional(),
    prioridade: PrioridadeDaTarefaSchema.optional(),
  })
  .strict();

export type AlterarTarefa = z.infer<typeof AlterarTarefaSchema>;

export const TarefaSchema = z.object({
  /**
   * Canônico, cunhado pela C13 — nunca o id do Twenty.
   *
   * `null` quando a tarefa nasceu na interface do próprio Twenty: a projeção
   * (D01) a traz, mas a cunhagem só acontece no `POST /v1/tasks`. Devolver o
   * id da engine aqui como se fosse canônico vazaria identificador de engine
   * no contrato (§3.2) — e quem quisesse agir sobre a tarefa usaria o id
   * errado. Para agir, use `referencia_externa` com o suporte; para operar
   * pela API, a tarefa precisa ter passado por ela.
   */
  id: IdSchema.nullable(),
  referencia_externa: z.string(),
  titulo: z.string(),
  corpo: z.string().nullable(),
  status: z.enum(['TODO', 'IN_PROGRESS', 'DONE']),
  prazo_em: InstanteSchema.nullable(),
  responsavel_referencia_externa: z.string().nullable(),
  /**
   * Sempre presente, nunca nula: a tarefa sem linha em `core.task_contexto`
   * — toda tarefa criada antes desta existir, e toda tarefa nascida dentro do
   * Twenty — sai como `normal`. Devolver nulo obrigaria cada tela a repetir a
   * mesma decisão, e uma delas a tomaria diferente.
   */
  prioridade: PrioridadeDaTarefaSchema,
  origem: z
    .object({
      tipo: OrigemDaTarefaSchema,
      referencia: z.string().nullable(),
      motivo: z.string().nullable(),
    })
    .nullable(),
  criada_em: InstanteSchema.nullable(),
  /**
   * De quem é a tarefa (#950): o cliente, com o nome e a conversa mais recente
   * para o atalho do cartão. Tarefa de oportunidade traz o cliente DELA. `null`
   * quando a tarefa não tem cliente, ou quando ele não é mais alcançável.
   */
  cliente: z
    .object({
      id: IdSchema,
      nome_exibicao: z.string().nullable(),
      conversa_id: IdSchema.nullable(),
    })
    .nullable(),
  /** A oportunidade, quando a tarefa foi criada para uma. */
  oportunidade: z.object({ id: IdSchema, titulo: z.string().nullable() }).nullable(),
  /**
   * Quem responde pela tarefa, para o avatar do cartão: o nome e a foto de
   * perfil (061), como no card do funil. `null` quando não tem dono; `nome`
   * nulo é o membro sem vínculo nesta organização.
   */
  responsavel: z
    .object({ nome: z.string().nullable(), foto_url: z.string().nullable() })
    .nullable(),
  /** Derivada com o expediente do tenant — ver o cabeçalho. */
  atrasada: z.boolean(),
  /**
   * Quando o atraso passou a contar. `null` quando não há prazo, ou quando o
   * tenant não tem expediente que permita afirmar atraso.
   */
  atrasada_desde: InstanteSchema.nullable(),
});

export type TarefaDaApi = z.infer<typeof TarefaSchema>;

export const PaginaDeTarefasSchema = z.object({
  itens: z.array(TarefaSchema),
  pagina: z.object({
    cursor_proximo: z.string().nullable(),
    tem_mais: z.boolean(),
    total_no_escopo: z.number().int(),
  }),
  /** Quantas das tarefas do escopo estão atrasadas — o número do topo da tela. */
  resumo: z.object({ atrasadas: z.number().int() }),
  frescor: z.object({
    stale: z.boolean(),
    projetado_em: InstanteSchema.nullable(),
  }),
});

export type PaginaDeTarefas = z.infer<typeof PaginaDeTarefasSchema>;

/**
 * `GET /v1/tasks/responsaveis` — a quem esta sessão pode atribuir uma tarefa.
 *
 * ── Por que existe uma lista própria, e nao a de administração ────────────
 *
 * `GET /v1/admin/pessoas` só responde a quem administra a conta. Quem mais
 * preenche o formulário de nova tarefa é o vendedor, e para ele aquela rota
 * responde 403 — o campo "responsável" ficaria vazio justamente para quem mais
 * precisa dele.
 *
 * ── Por que nao é o `ResponsavelPossivel` da transferência ────────────────
 *
 * Aquele schema é `.strict()` e serve `/v1/conversations/{id}/responsaveis` e
 * `/v1/opportunities/{id}/responsaveis`. Ele identifica a pessoa por
 * `usuario_id`, porque é assim que aquelas duas rotas recebem a atribuição.
 *
 * A tarefa recebe `responsavel_referencia_externa` — o id do membro na engine.
 * Devolver `usuario_id` aqui obrigaria a tela a traduzir um no outro, e ela
 * nao tem como: a correspondência vive em `core.memberships`, que a tela nao
 * lê. Esticar aquele schema para caber os dois casos mudaria a resposta das
 * outras duas rotas por causa desta.
 *
 * O que é COMPARTILHADO é o vocabulário: `MotivoDeNaoReceberSchema` é o mesmo,
 * para que "sem cadastro no CRM" seja a mesma frase nas três telas.
 *
 * ── Quem nao pode receber vem na lista assim mesmo ────────────────────────
 *
 * Pela razão que a transferência já registrou: esconder responde "nao existe"
 * a quem pergunta "cadê o Fulano?", e a resposta certa é "existe, e falta
 * isto". A diferença é que aqui só um impedimento se aplica — tarefa nao toca
 * o motor de conversa, entao `sem_acesso_ao_atendimento` nunca aparece.
 *
 * ── O que ela nao traz ────────────────────────────────────────────────────
 *
 * Sem e-mail e sem telefone. Para escolher uma pessoa bastam o nome e o
 * identificador; mandar o contato da equipe inteira para montar um `<select>`
 * é expor dado pessoal por comodidade de layout.
 */
export const ResponsavelDeTarefaSchema = z
  .object({
    /**
     * Id do membro na engine — o mesmo que entra em
     * `responsavel_referencia_externa` na criação. `null` para quem ainda nao
     * foi provisionado, que é exatamente quem nao pode receber.
     */
    referencia_externa: z.string().nullable(),
    nome: z.string(),
    papel: z.string(),
    pode_receber: z.boolean(),
    /** `null` quando pode receber. Preenchido é o que a tela mostra ao lado do nome. */
    motivo: MotivoDeNaoReceberSchema.nullable(),
    /** Verdadeiro para quem está pedindo a lista: a tela marca "(você)" e o põe primeiro. */
    sou_eu: z.boolean(),
  })
  .strict();

export type ResponsavelDeTarefa = z.infer<typeof ResponsavelDeTarefaSchema>;

export const ResponsaveisDeTarefaSchema = z
  .object({ itens: z.array(ResponsavelDeTarefaSchema) })
  .strict();

export type ResponsaveisDeTarefa = z.infer<typeof ResponsaveisDeTarefaSchema>;
